/**
 * staleTime policy
 * ----------------
 * The global default below is `Infinity`, so every `useQuery(...)` call in
 * `client/src/` MUST set an explicit `staleTime`. Without one, data is treated
 * as fresh forever and never refetches, which is rarely what you want.
 *
 * Pick the tier that matches the data:
 *   - 30_000   (30s)  -> live, user-mutable data (tasks, leads,
 *                        goals, anything the user edits in-app and expects to
 *                        see propagate quickly across tabs/views).
 *   - 60_000   (60s)  -> user settings, dashboard layout, email config -- data
 *                        that changes occasionally and via deliberate action.
 *   - 300_000  (5m)   -> taxonomy & admin data (sources, channels, directions,
 *                        contact types, pipeline stages, goal phases,
 *                        admin user lists) -- effectively config-shaped data.
 *
 * If you genuinely want "never auto-refetch" semantics, pass `staleTime: Infinity`
 * explicitly so future readers know it was a deliberate choice.
 *
 * This policy is enforced by `script/check-stale-time.ts`, which runs via
 * `npm run lint:stale-time` and as part of `npm run check`.
 */
import { MutationCache, QueryClient, QueryFunction } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

/**
 * QUERY FRESHNESS POLICY
 *
 * Background polling and window-focus refetches are disabled globally
 * (`refetchInterval: false`, `refetchOnWindowFocus: false`). Default
 * `staleTime` is `Infinity` so data is treated as fresh until an explicit
 * `invalidateQueries` is called (typically after a mutation).
 *
 * Per-query freshness opt-in: every `useQuery` callsite MUST set its own
 * explicit `staleTime`. Use these tiers:
 *   - `30_000`  (30s)  — live data: leads, tasks, goals, analytics
 *   - `60_000`  (60s)  — settings, settings/email
 *   - `300_000` (5m)   — taxonomy + admin: sources, directions, channels,
 *                        contact-types, *-categories, *-phases,
 *                        pipeline-stages, admin/users
 *   - `Infinity`       — auth-role guards (e.g. /api/auth/me)
 *
 * Per-query `staleTime` works as expected: when data goes stale, default
 * React Query behavior (refetch on remount, refetch on reconnect) will
 * refresh it. We deliberately do NOT disable `refetchOnMount` /
 * `refetchOnReconnect` globally so this opt-in remains effective.
 *
 * A repo-level guard (`script/check-stale-time.ts`, wired as the `queries`
 * validation workflow and `npm run check`) fails CI if a `useQuery` callsite
 * is missing `staleTime`.
 *
 * GLOBAL MUTATION ERROR HANDLING
 *
 * Any mutation that does not define its own `onError` will surface a
 * destructive toast so failures never disappear silently. When a mutation
 * provides a local `onError` (or a per-call `onError` via `mutate`/`mutateAsync`)
 * we skip the global toast so users do not see duplicate messages.
 */
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    if (mutation.options.onError) {
      return;
    }
    const message =
      error instanceof Error && error.message
        ? error.message
        : "Something went wrong. Please try again.";
    toast({
      title: "Save failed",
      description: message,
      variant: "destructive",
    });
  },
});

/**
 * Shared retry policy for polling/guard queries (e.g. /api/auth/me).
 *
 * Rate-limit lockout guard: NEVER retry a 429. The per-IP API limiter is
 * Postgres-backed and persists across the window, so a client that retries a
 * 429 at full speed can keep the limit tripped indefinitely and lock itself
 * into a permanent loading state. On 429 we give up immediately and let the
 * caller show the last-known state; the server-side `skipFailedRequests` guard
 * lets the window drain so the next natural refetch succeeds.
 *
 * For all other errors we retry up to `max` times with exponential backoff.
 * The error must carry a numeric `status` (set by the queryFn) for the 429
 * check to work.
 */
export function retryUnlessRateLimited(max = 1) {
  return (failureCount: number, error: unknown): boolean => {
    const status =
      (error as { status?: number } | null)?.status ??
      (error instanceof Error
        ? Number(error.message.match(/\b(\d{3})\b/)?.[1])
        : NaN);
    if (status === 429) return false;
    return failureCount < max;
  };
}

// Exponential backoff capped at 30s, so retries (and any refetch storms) slow
// down instead of hammering the server.
export function backoffRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 30_000);
}

export const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
      staleTime: 30_000,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
